import React, { useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { gql, useQuery } from '@apollo/client';
import DashboardView from '../components/DashboardView';
import { DASHBOARD_WEEK_FIELDS, LocationReport } from './Dashboard';
import { DashboardWeek } from '../models/dashboard';
import { weekRange } from '../lib/dashboard';

// Weekly dashboard (GRI-957), admins only: the client's dashboard built from the live draft of a
// week, before it is published. /dashboard/admin/preview/:companyId/:weekStart (all locations)
// and …/locations/:locationId (one location, with its audit sections read live). Behind the draft
// come the company's published weeks before it (up to 8 weeks in all), as the client will see them.
export const DRAFT_PREVIEW_QUERY = gql`
  query DashboardDraftPreview($companyId: ID!, $weekStart: String!) {
    dashboardDraft(companyId: $companyId, weekStart: $weekStart) {
      ${DASHBOARD_WEEK_FIELDS}
    }
  }
`;

// The published weeks of the company, for the trend and history behind the draft.
export const PUBLISHED_HISTORY_QUERY = gql`
  query DashboardPublishedHistory($companyId: ID!) {
    dashboard(weeks: 8, companyId: $companyId) {
      weeks {
        ${DASHBOARD_WEEK_FIELDS}
      }
    }
  }
`;

const DashboardPreview = () => {
  const { companyId, weekStart, locationId } =
    useParams<{
      companyId: string;
      weekStart: string;
      locationId?: string;
    }>();
  const history = useHistory();
  const { data, loading, error } = useQuery<{ dashboardDraft: DashboardWeek }>(
    DRAFT_PREVIEW_QUERY,
    { variables: { companyId, weekStart }, fetchPolicy: 'cache-first' }
  );
  // Without the history (an error, or nothing published yet) the draft shows alone.
  const { data: published } = useQuery<{
    dashboard: { weeks: DashboardWeek[] } | null;
  }>(PUBLISHED_HISTORY_QUERY, { variables: { companyId } });
  const [selected, setSelected] = useState(0);
  const base = `/dashboard/admin/preview/${companyId}/${weekStart}`;

  if (loading) return <Message>Building the draft…</Message>;
  if (error || !data) {
    return <Message>Couldn’t build the draft: {error?.message}</Message>;
  }
  // The draft replaces its week if it was already published (a republish).
  const weeks = [
    data.dashboardDraft,
    ...(published?.dashboard?.weeks || []).filter(
      (w) => w.weekStart < data.dashboardDraft.weekStart
    ),
  ].slice(0, 8);
  const week = Math.min(selected, weeks.length - 1);
  return (
    <>
      <Banner role="status">
        <span>
          <strong>Draft preview</strong> · {weekRange(data.dashboardDraft)} ·
          live data, not published. This is what the client will see once it is
          published.
        </span>
        <Link to="/dashboard/admin">Back to publishing</Link>
      </Banner>
      {locationId ? (
        <LocationReport
          // The draft week's sections are read live; older weeks are the published ones.
          draft={week === 0}
          weeks={weeks}
          selected={week}
          onSelect={setSelected}
          locationId={locationId}
          onBack={() => history.push(base)}
        />
      ) : (
        <DashboardView
          weeks={weeks}
          selected={week}
          onSelect={setSelected}
          reportLink={(id) => `${base}/locations/${encodeURIComponent(id)}`}
        />
      )}
    </>
  );
};

const Banner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  max-width: 1152px;
  margin: 24px auto 0;
  padding: 12px 16px;
  border: 1px solid #e6c97a;
  border-radius: 8px;
  background: #fff8e1;
  color: #5c4500;
  font-family: Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
  a {
    color: #325882;
    font-weight: 500;
  }
  @media (max-width: 640px) {
    margin: 16px 16px 0;
  }
`;

const Message = styled.p`
  margin: 44px 52px 0;
  color: #595959;
  font-family: Roboto, Helvetica, Arial, sans-serif;
`;

export default DashboardPreview;
